// src/components/VaccineReservationList.js
import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  Spinner,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  useBreakpointValue,
  Flex,
  Tooltip,
  IconButton,
  Grid,
  GridItem,
  FormControl,
  FormLabel,
  Select,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines, faSearch } from "@fortawesome/free-solid-svg-icons";
import DataTable from "./AppDataTable";
import {
  tableCustomStyles,
  ReservationStatusDot,
} from "../themes/TableStyles";
import apiService from "../services/ApiService";
import { useAuth } from "../context/AuthContext";
import * as IconButtonSet from "../themes/IconButtonSet";
import { StandardHeader } from "../themes/StandardHeader";
import {
  getValidPeriods,
  getEndPeriodOptions,
  isPeriodAfter,
} from "../utils/deliveryPeriod";

const emptyPeriodFilter = {
  StartDeliveryPeriod: "",
  EndDeliveryPeriod: "",
};

// ReservationDate ถูกบันทึกเป็น SQL DATE จึงไม่มีส่วนของเวลา แสดงเฉพาะวันที่
const formatReservationDate = (value) => {
  if (!value) return "-";
  const normalized =
    typeof value === "string" && value.endsWith("Z")
      ? value.slice(0, -1)
      : value;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("th-TH", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const StatusBadgeWithDot = ({ status }) => (
  <ReservationStatusDot
    status={status}
    fontSize={{ base: "sm", xl: "md" }}
  />
);

// NEW: สร้าง Component สำหรับแสดงผลแบบ Card List บน Mobile
const MobileReservationList = ({ data, pickupPoints, onOpenDetail }) => {
  if (data.length === 0) {
    return (
      <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
        ไม่พบข้อมูลที่ค้นหา
      </Text>
    );
  }

  return (
    <VStack spacing={4} w="100%">
      {data.map((row) => {
        const point = pickupPoints.find(
          (p) => p.WarehouseID === row.PickupPoint
        );
        const pickupPointName = point
          ? point.WarehouseName
          : row.PickupPoint || "-";

        return (
          <Box
            key={row.ReservationID}
            w="100%"
            p={{ base: 3, sm: 4 }}
            borderWidth={1}
            borderRadius="lg"
            boxShadow="sm"
          >
            <VStack align="start" spacing={3}>
              {/* --- บรรทัดแรก: เลขที่, สถานะ, ปุ่ม --- */}
              <Flex
                w="100%"
                justify="space-between"
                align="flex-start"
                gap={2}
                minW={0}
              >
                <VStack align="start" spacing={1} minW={0} flex="1">
                  <Text fontWeight="bold" fontSize="md" noOfLines={1}>
                    {row.ReservationID}
                  </Text>
                  <StatusBadgeWithDot status={row.ReservationStatus} />
                </VStack>
                <Tooltip label="ดูรายละเอียด" placement="top" hasArrow>
                  <IconButton
                    icon={<FontAwesomeIcon icon={faFileLines} />}
                    size="sm"
                    variant="ghost"
                    colorScheme="blue"
                    aria-label="ดูรายละเอียด"
                    flexShrink={0}
                    onClick={() => onOpenDetail(row.ReservationID)}
                  />
                </Tooltip>
              </Flex>

              {/* --- บรรทัดที่สอง: ข้อมูลเพิ่มเติม --- */}
              <Box w="100%" pt={2} borderTopWidth="1px" borderColor="gray.200">
                <Grid
                  templateColumns="auto 1fr"
                  gapX={2}
                  gapY={1}
                  fontSize="sm"
                  color="gray.600"
                >
                  <GridItem fontWeight="medium">จุดจำหน่าย:</GridItem>
                  <GridItem>{pickupPointName}</GridItem>

                  <GridItem fontWeight="medium">รอบจัดส่ง:</GridItem>
                  <GridItem>{row.DeliveryPeriod || "-"}</GridItem>

                  <GridItem fontWeight="medium">วันที่จอง:</GridItem>
                  <GridItem>
                    {formatReservationDate(row.ReservationDate)}
                  </GridItem>
                </Grid>
              </Box>
            </VStack>
          </Box>
        );
      })}
    </VStack>
  );
};

const VaccineReservationList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [filteredReservations, setFilteredReservations] = useState([]);
  const [pickupPoints, setPickupPoints] = useState([]);
  const [deliveryPeriods, setDeliveryPeriods] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [periodFilter, setPeriodFilter] = useState(emptyPeriodFilter);

  // ต่ำกว่า lg ตารางจะแน่นเกินไป จึงสลับไปใช้การ์ดแทน
  const isMobile = useBreakpointValue(
    { base: true, lg: false },
    { ssr: false }
  );

  useEffect(() => {
    if (!sessionId) {
      navigate("/login", { state: { from: location.pathname } });
    }
  }, [sessionId, navigate, location.pathname]);

  // Columns สำหรับ Desktop เท่านั้น
  const columns = [
    {
      name: "เลขที่ใบจอง",
      selector: (row) => row.ReservationID,
      sortable: true,
      minWidth: "140px",
      grow: 0,
    },
    {
      name: "จุดจำหน่าย",
      selector: (row) => {
        const point = pickupPoints.find(
          (p) => p.WarehouseID === row.PickupPoint
        );
        return point ? point.WarehouseName : row.PickupPoint || "-";
      },
      sortable: true,
      minWidth: "180px",
      wrap: true,
    },
    {
      name: "รอบการจัดส่ง",
      selector: (row) => row.DeliveryPeriod || "-",
      sortable: true,
      minWidth: "140px",
      wrap: true,
      hide: 1280,
    },
    {
      name: "วันที่จอง",
      // เรียงลำดับด้วยค่าดิบเพื่อให้เรียงตามเวลาจริง ไม่ใช่ตามตัวอักษร
      selector: (row) => row.ReservationDate || "",
      format: (row) => formatReservationDate(row.ReservationDate),
      sortable: true,
      minWidth: "130px",
      grow: 0,
    },
    {
      name: "สถานะ",
      cell: (row) => <StatusBadgeWithDot status={row.ReservationStatus} />,
      selector: (row) => row.ReservationStatus,
      sortable: true,
      minWidth: "150px",
    },
    {
      name: "การดำเนินการ",
      cell: (row) => (
        <IconButtonSet.View
          icon={faFileLines}
          onClick={() => openReservationDetail(row.ReservationID)}
          label="รายละเอียด" // บน Desktop ยังคงมี Label
          size="sm"
        />
      ),
      ignoreRowClick: true,
      button: true,
      minWidth: "140px",
    },
  ];

  const fetchPickupPoints = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinePickupPointList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setPickupPoints(response.VaccineWarehousesList || []);
      }
    } catch (error) {
      console.error("Error fetching pickup points:", error);
    }
  };

  const fetchDeliveryPeriods = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineReservationPeriodList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setDeliveryPeriods(getValidPeriods(response.DeliveryPeriods || []));
      }
    } catch (error) {
      console.error("Error fetching delivery periods:", error);
    }
  };

  // กรองด้วยคำค้นหาที่ฝั่งหน้าเว็บ ส่วนรอบการจัดส่งกรองที่ฝั่งเซิร์ฟเวอร์
  const filterByKeyword = (rows, keyword, points) => {
    if (!keyword) return rows;

    const searchTerms = keyword.split(" ").filter((term) => term.length > 0);
    return rows.filter((row) => {
      const pickupPointName =
        points.find((p) => p.WarehouseID === row.PickupPoint)?.WarehouseName ||
        row.PickupPoint ||
        "";

      return searchTerms.every((term) =>
        [
          row.ReservationID.toLowerCase(),
          row.FarmerName ? row.FarmerName.toLowerCase() : "",
          row.DeliveryPeriod ? row.DeliveryPeriod.toLowerCase() : "",
          pickupPointName.toLowerCase(),
          row.ReservationStatus.toLowerCase(),
        ].some((field) => field.includes(term))
      );
    });
  };

  const fetchReservations = async (
    filters = periodFilter,
    keyword = searchTerm
  ) => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineReservationsList",
        data: {
          StartDeliveryPeriod: filters.StartDeliveryPeriod || "",
          EndDeliveryPeriod: filters.EndDeliveryPeriod || "",
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const data = response.VaccineReservationsList || [];
        setReservations(data);
        setFilteredReservations(filterByKeyword(data, keyword, pickupPoints));
      }
    } catch (error) {
      console.error("Error fetching reservations:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const initPage = async () => {
      if (sessionId) {
        await Promise.all([fetchPickupPoints(), fetchDeliveryPeriods()]);
        await fetchReservations(emptyPeriodFilter, "");
      }
    };
    initPage();
  }, [sessionId]);

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    setFilteredReservations(filterByKeyword(reservations, value, pickupPoints));
  };

  const handlePeriodChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...periodFilter, [name]: value };

    // ถ้ารอบเริ่มต้นใหม่อยู่หลังรอบสิ้นสุดที่เลือกไว้ ให้เลื่อนรอบสิ้นสุดตามไปด้วย
    if (
      name === "StartDeliveryPeriod" &&
      value &&
      updated.EndDeliveryPeriod &&
      isPeriodAfter(deliveryPeriods, value, updated.EndDeliveryPeriod)
    ) {
      updated.EndDeliveryPeriod = value;
    }

    setPeriodFilter(updated);
    fetchReservations(updated, searchTerm);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setPeriodFilter(emptyPeriodFilter);
    fetchReservations(emptyPeriodFilter, "");
  };

  const handleCreateNew = () => {
    navigate("/vaccinereservation/new");
  };

  // ส่งเลขที่ใบจองผ่าน state ของ router เพื่อไม่ให้เลขที่ใบจองปรากฏใน URL
  const openReservationDetail = (reservationID) => {
    navigate("/vaccinereservationdetail", { state: { reservationId: reservationID } });
  };

  const endPeriodOptions = getEndPeriodOptions(
    deliveryPeriods,
    periodFilter.StartDeliveryPeriod
  );

  return (
    <Box
      p={{ base: 3, sm: 4, md: 6 }}
      m={{ base: 2, md: 4 }}
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
    >
      <VStack spacing={{ base: 4, md: 6 }} align="start">
        <StandardHeader
          title="รายการใบจองวัคซีน"
          bgColor="iBlue.600"
          icon={faFileLines}
        />
        <Flex
          w="100%"
          gap={3}
          direction={{ base: "column", xl: "row" }}
          align={{ base: "stretch", xl: "flex-end" }}
          justify="space-between"
        >
          <Grid
            w="100%"
            flex="1"
            minW={0}
            gap={3}
            templateColumns={{
              base: "1fr",
              md: "repeat(2, minmax(0, 1fr))",
              xl: "repeat(3, minmax(0, 1fr))",
            }}
          >
            <FormControl>
              <FormLabel
                fontSize={{ base: "sm", md: "md" }}
                color="gray.600"
                mb={1}
              >
                รอบการจัดส่ง (ตั้งแต่)
              </FormLabel>
              <Select
                name="StartDeliveryPeriod"
                value={periodFilter.StartDeliveryPeriod}
                onChange={handlePeriodChange}
                placeholder="ทุกรอบ"
                colorScheme="iBlue"
              >
                {deliveryPeriods.map((period) => (
                  <option key={period.DeliveryPeriod} value={period.DeliveryPeriod}>
                    {period.DeliveryPeriod}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel
                fontSize={{ base: "sm", md: "md" }}
                color="gray.600"
                mb={1}
              >
                รอบการจัดส่ง (ถึง)
              </FormLabel>
              <Select
                name="EndDeliveryPeriod"
                value={periodFilter.EndDeliveryPeriod}
                onChange={handlePeriodChange}
                placeholder="ทุกรอบ"
                colorScheme="iBlue"
              >
                {endPeriodOptions.map((period) => (
                  <option key={period.DeliveryPeriod} value={period.DeliveryPeriod}>
                    {period.DeliveryPeriod}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl gridColumn={{ md: "span 2", xl: "auto" }}>
              <FormLabel
                fontSize={{ base: "sm", md: "md" }}
                color="gray.600"
                mb={1}
              >
                ค้นหาใบจอง
              </FormLabel>
              <Flex gap={2} align="center">
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <FontAwesomeIcon icon={faSearch} color="#A0AEC0" />
                  </InputLeftElement>
                  <Input
                    placeholder="เลขที่ใบจอง จุดจำหน่าย สถานะ..."
                    value={searchTerm}
                    onChange={handleSearch}
                    colorScheme="iBlue"
                  />
                </InputGroup>
                <IconButtonSet.Clear
                  onClick={handleClearSearch}
                  flexShrink={0}
                />
              </Flex>
            </FormControl>
          </Grid>
          <IconButtonSet.Add
            onClick={handleCreateNew}
            label="สร้างใบจองใหม่"
            flexShrink={0}
            w={{ base: "100%", xl: "auto" }}
          />
        </Flex>

        {/* --- NEW: Conditional Rendering --- */}
        {isLoading ? (
          <Flex justify="center" w="100%">
            <Spinner size="xl" />
          </Flex>
        ) : reservations.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            {periodFilter.StartDeliveryPeriod || periodFilter.EndDeliveryPeriod
              ? "ไม่พบใบจองวัคซีนในรอบการจัดส่งที่เลือก"
              : "ยังไม่มีข้อมูลใบจองวัคซีน"}
          </Text>
        ) : isMobile ? (
          <MobileReservationList
            data={filteredReservations}
            pickupPoints={pickupPoints}
            onOpenDetail={openReservationDetail}
          />
        ) : (
          <Box w="100%" overflowX="auto">
            <DataTable
              columns={columns}
              data={filteredReservations}
              pagination
              highlightOnHover
              striped
              customStyles={tableCustomStyles}
            />
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default VaccineReservationList;
